import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import {
  getReferralConfig,
  updateBonusConfig,
  updateShareConfig,
  getReferralConfigHistory,
  ReferralConfigResponse,
  BonusConfig,
  ShareConfig,
  UpdateBonusConfigPayload,
  UpdateShareConfigPayload,
  ReferralHistoryItem,
  GetReferralHistoryParams,
} from '@/api/referralConfig';

export function useReferralConfig() {
  return useQuery<ReferralConfigResponse, AxiosError<{ message?: string }>>({
    queryKey: ['referral-config'],
    queryFn: getReferralConfig,
  });
}

export function useReferralConfigHistory(params?: GetReferralHistoryParams) {
  return useQuery<ReferralHistoryItem[], AxiosError<{ message?: string }>>({
    queryKey: ['referral-config-history', params],
    queryFn: () => getReferralConfigHistory(params),
    placeholderData: (previousData) => previousData,
  });
}

export function useUpdateBonusConfig() {
  const queryClient = useQueryClient();

  return useMutation<BonusConfig, AxiosError<{ message?: string }>, UpdateBonusConfigPayload>({
    mutationFn: updateBonusConfig,
    onSuccess: (updatedBonusConfig) => {
      queryClient.setQueryData<ReferralConfigResponse | undefined>(['referral-config'], (oldData) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          bonusConfig: updatedBonusConfig,
        };
      });
      queryClient.invalidateQueries({ queryKey: ['referral-config'] });
      queryClient.invalidateQueries({ queryKey: ['referral-config-history'] });
    },
  });
}

export function useUpdateShareConfig() {
  const queryClient = useQueryClient();

  return useMutation<ShareConfig, AxiosError<{ message?: string }>, UpdateShareConfigPayload>({
    mutationFn: updateShareConfig,
    onSuccess: (updatedShareConfig) => {
      queryClient.setQueryData<ReferralConfigResponse | undefined>(['referral-config'], (oldData) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          shareConfig: updatedShareConfig,
        };
      });
      queryClient.invalidateQueries({ queryKey: ['referral-config'] });
      queryClient.invalidateQueries({ queryKey: ['referral-config-history'] });
    },
  });
}

